import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { AuthErrorBrand } from './AuthError';

/** Shape of the resend endpoint's answer — mirrors the Directory response. */
export interface ResendVerificationResult {
  sent: boolean;
  message?: string | null;
  /** Seconds before another send is accepted. */
  retryAfterSeconds: number;
}

interface Props {
  children: ReactNode;
  /** App identity shown on the gate screen — matches the sidebar brand. */
  brand?: AuthErrorBrand;
  /**
   * Re-sends the verification email to the signed-in user. Injected rather
   * than imported: `@my/directory-api` depends on this package, so reaching
   * the other way would be a cycle.
   */
  onResend: () => Promise<ResendVerificationResult>;
  /** Seconds between automatic re-checks. */
  pollSeconds?: number;
}

/**
 * Blocks the app until the signed-in user's email address is verified.
 *
 * <p>
 * Mount INSIDE {@link RequireAuth} — it assumes a session already exists and
 * reads `email_verified` off the ID token. Auth0 sets that claim on the
 * self-signup path only: an admin-invited user's password-change ticket is
 * minted with `mark_email_as_verified`, so they arrive already verified and
 * never see this screen.
 * </p>
 *
 * <p>
 * <b>The claim is only trusted when it is explicitly `false`.</b> A missing
 * claim renders the children. That is deliberate: the failure mode of a
 * misconfigured tenant or a changed scope should be "the gate stops gating",
 * not "every user is locked out of a product they pay for".
 * </p>
 *
 * <p>
 * Re-checking asks Auth0 for a fresh token with `cacheMode: 'off'`;
 * `getAccessTokenSilently` refreshes the provider's `user` as a side effect
 * (it dispatches the new `client.getUser()` result), so the claim updates
 * without a page reload and without any endpoint of our own.
 * </p>
 */
export function RequireVerifiedEmail({
  children,
  brand,
  onResend,
  pollSeconds = 15,
}: Props) {
  const { user, getAccessTokenSilently, logout } = useAuth0();
  const [checking, setChecking] = useState(false);
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);

  // Only an explicit `false` gates — see the note on the component.
  const blocked = user?.email_verified === false;
  const BrandIcon = brand?.icon;

  const recheck = useCallback(async () => {
    setChecking(true);
    try {
      await getAccessTokenSilently({ cacheMode: 'off' });
    } catch {
      // A failed refresh is not worth surfacing: the poll will try again, and
      // a genuinely dead session is RequireAuth's problem, not this screen's.
    } finally {
      setChecking(false);
    }
  }, [getAccessTokenSilently]);

  // Background poll, so a user who clicks the link in their mail client finds
  // the app already unlocked when they switch back. Paused while the tab is
  // hidden — a forgotten tab shouldn't sit and hammer Auth0 all day — and it
  // re-checks immediately on becoming visible again, which is the exact moment
  // the user has come back from their inbox.
  const pollRef = useRef(recheck);
  pollRef.current = recheck;
  useEffect(() => {
    if (!blocked) return;
    const tick = () => {
      if (document.visibilityState === 'visible') void pollRef.current();
    };
    const id = window.setInterval(tick, pollSeconds * 1000);
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [blocked, pollSeconds]);

  // Cooldown countdown for the resend button.
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setInterval(() => setCooldown((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => window.clearInterval(id);
  }, [cooldown]);

  const resend = async () => {
    setSending(true);
    setNotice(null);
    try {
      const result = await onResend();
      setCooldown(result.retryAfterSeconds);
      if (result.sent) {
        setNotice('Sent. Check your inbox — and your spam folder.');
      } else {
        // Not an error: most often "already verified", in which case the
        // re-check below lets the user straight through.
        setNotice(result.message ?? 'Nothing to send.');
        void recheck();
      }
    } catch (err) {
      setNotice(
        err instanceof Error && err.message
          ? err.message
          : 'Could not send the email. Please try again.',
      );
    } finally {
      setSending(false);
    }
  };

  if (!blocked) return <>{children}</>;

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-800 px-6">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-2xl sm:p-10">
        {/* Brand lockup — same as the sign-in error screen, so this reads as a
            continuation of the sign-in flow rather than an app screen. */}
        <div className="mb-7 flex items-center justify-center gap-2.5">
          {BrandIcon && (
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--color-primary)] text-white shadow-sm">
              <BrandIcon className="h-5 w-5" />
            </span>
          )}
          <span className="text-lg font-semibold tracking-tight text-slate-800">
            {brand?.title ?? 'My Suite'}
          </span>
        </div>

        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-[var(--color-primary)]">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-6 w-6"
            aria-hidden="true"
          >
            <rect width="20" height="16" x="2" y="4" rx="2" />
            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
          </svg>
        </div>

        <h1 className="text-xl font-semibold text-slate-900">Confirm your email</h1>
        <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-slate-500">
          We sent a link to{' '}
          <span className="font-medium text-slate-700">{user?.email ?? 'your address'}</span>. Click
          it to finish setting up your account.
        </p>

        <div className="mt-7 space-y-2">
          <button
            type="button"
            onClick={() => void recheck()}
            disabled={checking}
            className="w-full rounded-lg bg-[var(--color-primary)] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            {checking ? 'Checking…' : 'I’ve confirmed it'}
          </button>
          <button
            type="button"
            onClick={() => void resend()}
            disabled={sending || cooldown > 0}
            className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-60 disabled:hover:bg-white"
          >
            {sending
              ? 'Sending…'
              : cooldown > 0
                ? `Resend in ${cooldown}s`
                : 'Resend the email'}
          </button>
          <button
            type="button"
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
            className="w-full px-4 py-2 text-sm font-medium text-slate-400 transition-colors hover:text-slate-600"
          >
            Log out
          </button>
        </div>

        {notice && <p className="mt-5 text-xs text-slate-500">{notice}</p>}
        <p className="mt-5 text-xs text-slate-400">
          This page unlocks on its own once you’ve confirmed.
        </p>
      </div>
    </div>
  );
}
